import { Button, Grid, Typography } from '@material-ui/core';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../../Session';
import { DealerQuotesStatus, FullQuotationDataFragment, LiveDealerQuotesDataFragment, UserType } from '../../../../api';
import Accordion from '../../../../components/Accordion';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import BooleanCheckbox from '../../../../components/fields/BooleanCheckbox';
import OutlinedDateTimePickerField from '../../../../components/fields/OutlinedDateTimePickerField';
import OutlinedInputField from '../../../../components/fields/OutlinedInputField';
import AuthorisationForLowerQuote from './AuthorisationForLowerQuote';
import DealerQuotes from './DealerQuotes';
import InviteQuote from './InviteQuote';
import { useTwoButtonsStyles } from './shared';

export type EditFormProps = {
    quotation: FullQuotationDataFragment;
    liveQuotation?: LiveDealerQuotesDataFragment;
    inSessionDealerIdsSet: string[];
    onAuthoriseLowerQuote: () => void;
    onCloseQuotation: () => void;
    onInviteDealers: () => void;
    onSaveQuotationDetails: () => void;
    onResetSelection: (resetSelection: () => void) => void;
    onResetLowerQuote: (reset: () => void) => void;
};

const EditForm = ({
    quotation,
    liveQuotation,
    inSessionDealerIdsSet,
    onAuthoriseLowerQuote,
    onCloseQuotation,
    onInviteDealers,
    onSaveQuotationDetails,
    onResetSelection,
    onResetLowerQuote,
}: EditFormProps) => {
    const { t } = useTranslation('purchaseTransactionsPage');
    const currentUser = useCurrentUser();
    const viewKey = currentUser.type === UserType.Approver ? 'approverView' : 'valuationTeamView';
    const dealersInfo = liveQuotation?.dealersInfo ?? quotation.dealersInfo ?? [];
    const submittedDealers = dealersInfo.filter(dealer => dealer.status === DealerQuotesStatus.Quoted).length;
    const totalDealers = dealersInfo.length;
    const sessionStarted = !dayjs(quotation.startPeriod).isAfter(dayjs()) || quotation.started;
    const [showCloseQuotationConfirmDialog, setShowCloseQuotationConfirmDialog] = useState(false);
    const styles = useTwoButtonsStyles();

    return (
        <Grid spacing={2} container>
            <Grid xs={12} item>
                <Accordion title={t(`purchaseTransactionsPage:accordionTitle.quotationDetails`)} defaultExpanded>
                    <Grid spacing={2} container>
                        <Grid lg={12} item>
                            <BooleanCheckbox
                                color="secondary"
                                disabled={quotation.closed}
                                label={t('quotation.mainInfo.hideVehicleNo')}
                                name="hideVehicleNo"
                                style={{ color: 'red' }}
                            />
                        </Grid>
                        <Grid lg={6} md={6} sm={12} xs={12} item>
                            <OutlinedDateTimePickerField
                                color="secondary"
                                disablePast={!sessionStarted}
                                disabled={sessionStarted || quotation.closed}
                                format={t('common:formats.dateTimePickerFormat')}
                                label={t('quotation.mainInfo.startDate')}
                                name="startPeriod"
                                placeholder={t('common:formats.dateTimePickerFormat')}
                                required
                            />
                        </Grid>
                        {quotation.closed && (
                            <Grid lg={6} md={6} sm={12} xs={12} item>
                                <OutlinedDateTimePickerField
                                    color="secondary"
                                    format={t('common:formats.dateTimePickerFormat')}
                                    label={t('quotation.mainInfo.endDate')}
                                    name="endPeriod"
                                    placeholder={t('common:formats.dateTimePickerFormat')}
                                    disabled
                                />
                            </Grid>
                        )}
                        <Grid xs={12} item>
                            <OutlinedInputField
                                color="secondary"
                                disabled={quotation.closed}
                                label={t('quotation.mainInfo.remarks')}
                                name="remarks"
                                placeholder={t('quotation.mainInfo.enterDescription')}
                                multiline
                            />
                        </Grid>
                        <Grid className={styles.actionButtonsContainer} xs={12} container item>
                            <Grid className={styles.saveButtonContainer} sm={6} xs={12} item>
                                <Button
                                    className={styles.actionButton}
                                    color="secondary"
                                    disabled={quotation.closed}
                                    onClick={onSaveQuotationDetails}
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                >
                                    {t(`purchaseTransactionsPage:quotation.mainInfo.saveQuotationDetails`)}
                                </Button>
                            </Grid>
                            {[UserType.Admin, UserType.Approver].includes(currentUser.type) && !quotation.closed && (
                                <Grid className={styles.closeButtonContainer} sm={6} xs={12} item>
                                    <Button
                                        className={styles.actionButton}
                                        color="secondary"
                                        onClick={() => setShowCloseQuotationConfirmDialog(true)}
                                        type="button"
                                        variant="outlined"
                                        fullWidth
                                    >
                                        {t('purchaseTransactionsPage:quotation.mainInfo.closeSession')}
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Accordion>
            </Grid>
            <Grid xs={12} item>
                <Accordion
                    title={t(`purchaseTransactionsPage:accordionTitle.dealerQuotes`)}
                    titleEnd={
                        <Typography color="textSecondary" variant="body2">
                            {t('quotation.dealerQuotes.numberSubmitted', {
                                submittedCount: submittedDealers,
                                totalDealers,
                            })}
                        </Typography>
                    }
                    defaultExpanded
                >
                    <DealerQuotes quotation={liveQuotation ?? quotation} />
                </Accordion>
            </Grid>
            <Grid xs={12} item>
                <Accordion
                    title={t(`purchaseTransactionsPage:quotation.authorisationForLowerQuote.title`)}
                    defaultExpanded
                >
                    {!quotation.closed ? (
                        <AuthorisationForLowerQuote
                            onReset={onResetLowerQuote}
                            onSubmit={onAuthoriseLowerQuote}
                            quotation={quotation}
                        />
                    ) : (
                        <Typography color="textSecondary">
                            {t('purchaseTransactionsPage:quotation.inviteQuote.quotationClosed')}
                        </Typography>
                    )}
                </Accordion>
            </Grid>
            <Grid xs={12} item>
                <Accordion title={t(`purchaseTransactionsPage:quotation.inviteQuote.${viewKey}.title`)} defaultExpanded>
                    {!quotation.closed ? (
                        <InviteQuote
                            ignoreInSessionFilter={currentUser.type === UserType.Approver}
                            inSessionDealerIds={inSessionDealerIdsSet}
                            inSessionTagIds={quotation.tagIds ?? []}
                            onResetSelection={onResetSelection}
                            onSubmit={onInviteDealers}
                            displayDealerTagNames
                        />
                    ) : (
                        <Typography color="textSecondary">
                            {t('purchaseTransactionsPage:quotation.inviteQuote.quotationClosed')}
                        </Typography>
                    )}
                </Accordion>
            </Grid>
            <ConfirmDialog
                body={t('purchaseTransactionsPage:quotation.closeSessionDialog.body')}
                setContinueConfirmDialog={() => {
                    setShowCloseQuotationConfirmDialog(false);
                    onCloseQuotation();
                }}
                setHideConfirmDialog={() => setShowCloseQuotationConfirmDialog(false)}
                show={showCloseQuotationConfirmDialog}
                title={t('purchaseTransactionsPage:quotation.closeSessionDialog.title')}
            />
        </Grid>
    );
};

export default EditForm;
